/**
 * League of Managers - Domain Types
 */

export type Position = 'POR' | 'DEF' | 'MED' | 'DEL';

export type MatchdayStatus = 'PLANNED' | 'OPEN' | 'LOCKED' | 'SCORED' | 'CLOSED';

export interface PlayerContract {
  salary: number; // M€ per year (e.g. 1.8 M€)
  durationYears: number; // e.g. 3 seasons
  releaseClause: number; // M€ (e.g. 45 M€)
  acquisitionCost: number; // M€ paid to acquire
  startSeason: number;
  endSeason: number;
  signingBonus?: number; // M€ prima de fichaje (para agentes libres)
}

export interface RealClubInfo {
  id: string;
  name: string;
  shortName: string;
}

export type UnavailabilityType = 'INJURY' | string;
export type UnavailabilityReason = 'Arthroscopy' | 'Wrist fracture' | 'Calf problems' | string;

export const UNAVAILABILITY_TYPE_LABELS: Record<string, string> = {
  INJURY: 'Lesión',
  SANCTION: 'Sanción',
  SUSPENSION: 'Suspensión',
  INTERNATIONAL: 'Convocatoria internacional',
  ILLNESS: 'Enfermedad',
};

export const UNAVAILABILITY_REASON_LABELS: Record<string, string> = {
  'Arthroscopy': 'Artroscopia',
  'arthroscopy': 'Artroscopia',
  'Wrist fracture': 'Fractura de muñeca',
  'wrist fracture': 'Fractura de muñeca',
  'Calf problems': 'Problemas en el gemelo',
  'calf problems': 'Problemas en el gemelo',
  'Calf strain': 'Sobrecarga en el gemelo',
  'Muscle injury': 'Lesión muscular',
  'Torn thigh muscle': 'Rotura muscular en el muslo',
  'Cruciate ligament tear': 'Rotura del ligamento cruzado',
  'Hamstring injury': 'Lesión en los isquiotibiales',
  'Intestinal virus': 'Virus intestinal',
  'intestinal virus': 'Virus intestinal',
  'Gastroenteritis': 'Gastroenteritis',
  'gastroenteritis': 'Gastroenteritis',
  'Suspension': 'Suspensión',
  'Sanction': 'Sanción',
};

export function getUnavailabilityTypeLabel(type?: string | null): string {
  if (!type) return '';
  const key = type.toUpperCase().trim();
  if (key === 'INJURY' || key === 'LESION' || key === 'LESIÓN') return 'Lesión';
  if (key === 'SANCTION' || key === 'SANCION' || key === 'SUSPENSION') return 'Sanción';
  return UNAVAILABILITY_TYPE_LABELS[key] || type;
}

export function getUnavailabilityReasonLabel(reason?: string | null): string {
  if (!reason) return '';
  const trimmed = reason.trim();
  if (UNAVAILABILITY_REASON_LABELS[trimmed]) {
    return UNAVAILABILITY_REASON_LABELS[trimmed];
  }
  const lower = trimmed.toLowerCase();
  for (const [key, label] of Object.entries(UNAVAILABILITY_REASON_LABELS)) {
    if (key.toLowerCase() === lower) {
      return label;
    }
  }
  return reason;
}

export interface PlayerUnavailability {
  type: UnavailabilityType;
  reason: UnavailabilityReason;
  startDate?: string;
  expectedEndDate?: string;
  status?: 'ACTIVE' | 'RESOLVED' | string;
  typeLabel?: string;
  reasonLabel?: string;
}

export interface Player {
  id: string;
  name: string;
  shortName: string;
  position: Position;
  overall?: number; // Opcional / en proceso de retiro
  age: number;
  nationality: string;
  isAmateur: boolean;
  clubId: string | null; // null if free agent
  realClub?: RealClubInfo;
  clubName?: string;
  clubShortName?: string;
  contract: PlayerContract;
  listedForTransfer: boolean;
  marketSource?: 'CLUB_LISTING';
  askingPrice?: number; // M€
  formScore: number; // 1 to 5
  averagePoints?: number | null;
  last3AveragePoints?: number | null;
  // Metadatos de la API / Backend
  playerInstanceId?: string;
  listingId?: string;
  playerDefinitionId?: string;
  rawPosition?: string;
  status?: string;
  playerType?: string;
  version?: number;
  currentMarketValue?: number;
  secondaryPositions?: string[];
  rating?: number | null;
  ratedAppearances?: number;
  unavailabilities?: PlayerUnavailability[];
  stats?: {
    pace: number;
    shooting: number;
    passing: number;
    dribbling: number;
    defending: number;
    physical: number;
  };
  condition?: number;
  morale?: string;
  potential?: number;
  attributes?: Record<string, number>;
}

export interface LineupSlot {
  slotId: string;
  position: Position;
  x: number; // Percentage on pitch (0-100)
  y: number; // Percentage on pitch (0-100)
  label: string;
  playerId: string | null;
}

export interface BackendFormationSlot {
  slot: number;
  assignedPosition: 'GOALKEEPER' | 'DEFENDER' | 'MIDFIELDER' | 'FORWARD' | string;
  label: string;
  x: number;
  y: number;
}

export interface BackendFormation {
  code: string;
  version: number;
  slots: BackendFormationSlot[];
}

export interface ClubLineupPlayer {
  slot: number;
  playerInstanceId: string;
  assignedPosition?: 'GOALKEEPER' | 'DEFENDER' | 'MIDFIELDER' | 'FORWARD' | string;
}

export interface LineupSelectionPlayer {
  slot: number;
  playerInstanceId: string;
}

export interface LineupSelectionResponse {
  clubId?: string;
  formation: string;
  formationVersion?: number;
  players: LineupSelectionPlayer[];
  bench: string[];
  version: number;
  code?: string;
  message?: string;
}

export interface SaveLineupSelectionPayload {
  formation: string;
  expectedVersion: number;
  players: LineupSelectionPlayer[];
  bench: string[];
}

export interface TacticalLineupPlayer {
  playerInstanceId: string;
  playerDefinitionId?: string;
  displayName?: string;
  position?: string;
  type?: string;
  slot?: number;
  benchPlace?: number;
  isAmateur?: boolean;
  retired?: boolean;
  realClub?: {
    id?: string;
    name?: string;
    shortName?: string;
  } | null;
}

export interface SubstitutionChange {
  outgoing: string;
  incoming: string;
}

export interface SubstitutionWindowChangeDetail {
  outgoing: string;
  incoming: string;
  slot?: number;
  benchPlace?: number;
  outgoingName?: string;
  incomingName?: string;
}

export interface SubstitutionWindow {
  windowId: string;
  ordinal: number;
  receivedAt?: string;
  confirmedAt?: string;
  revisionBefore: number;
  revisionAfter: number;
  changes: SubstitutionWindowChangeDetail[];
}

export interface TacticalLineup {
  snapshotId?: string;
  fixtureId?: string;
  clubId: string;
  matchdayId: string;
  formation: string;
  starters: TacticalLineupPlayer[];
  bench: TacticalLineupPlayer[];
  revision: number;
  windowsUsed: number;
  substitutionsUsed: number;
  windows?: SubstitutionWindow[];
}

export interface TacticalLineupResponse {
  lineup: TacticalLineup;
  retired: string[];
  windowsRemaining: number;
  substitutionsRemaining: number;
}

export interface ConfirmSubstitutionWindowPayload {
  expectedRevision: number;
  changes: SubstitutionChange[];
}

export interface SubstitutionWindowResponse {
  windowId: string;
  ordinal: number;
  receivedAt?: string;
  confirmedAt?: string;
  revisionBefore: number;
  revisionAfter: number;
  changes: SubstitutionWindowChangeDetail[];
}

export interface ClubLineupResponse {
  clubId?: string;
  ratingSeasonId?: string;
  formation: string;
  formationVersion?: number;
  version?: number;
  players: ClubLineupPlayer[];
  bench?: string[];
  code?: string;
  message?: string;
  requestId?: string;
}

export interface SaveLineupPayload {
  formation: string;
  expectedVersion?: number;
  players: {
    slot: number;
    playerInstanceId: string;
  }[];
  bench?: string[];
}

export interface ClubStaff {
  goalkeeping: number; // 1 to 5 (supports half stars: 1.0, 1.5, 2.0...)
  fitness: number;
  technical: number;
  shooting: number;
}

export interface StadiumCommercialModules {
  parkingLevel: number; // 1 to 5
  cafeteriaLevel: number; // 1 to 5
  merchandisingLevel: number; // 1 to 5
  bathroomLevel?: number; // 1 to 5
}

export interface Stadium {
  name: string;
  capacity: number; // 15000, 20000, 30000, 45000, 65000
  seasonTicketPrice: number; // € per season ticket (e.g. 280 €)
  seasonTicketHolders: number; // Max 70% of capacity
  ticketPrice: number; // € per matchday general ticket (e.g. 35 €)
  modules: StadiumCommercialModules;
  standsLevel?: number;
  ratingLevel?: number;
  facilitiesRatingLevel?: number;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
}

export interface BankLoan {
  id: string;
  principal: number; // M€ (max 25 M€)
  remaining: number; // M€
  annualInterestRate: number; // 0.06 (6%)
  termSeasons: number; // 1 to 3
  seasonalInstallment: number; // M€
  takenOnSeason: number;
}

// -------------------------------------------------------------
// Créditos por Jornada (Matchday Loans) - Contratos oficiales
// -------------------------------------------------------------

export type LoanIneligibilityReason =
  | 'NO_CALENDAR'
  | 'EXISTING_DEBT'
  | 'CLOSING'
  | 'SEASON_END'
  | string;

export type LoanTier = 'TOP' | 'MIDDLE' | 'BOTTOM' | string;

export interface LoanOfferCalendar {
  currentMatchday?: number;
  remainingMatchdays?: number;
  remaining?: number;
  totalMatchdays?: number;
  seasonId?: string;
  cycle?: number;
  round?: number;
}

export type LoanMovementType =
  | 'DISBURSEMENT'
  | 'INTEREST'
  | 'VOLUNTARY_REPAYMENT'
  | 'MATURITY_REPAYMENT'
  | string;

export interface LoanMovement {
  id?: string;
  movementId?: string;
  type: LoanMovementType;
  amount: number; // en EUR
  interestAmount?: number;
  principalAmount?: number;
  createdAt?: string;
  date?: string;
  occurredAt?: string;
  matchday?: number;
  matchdayNumber?: number;
  description?: string;
  balanceAfter?: number;
}

export interface ActiveMatchdayLoan {
  id: string;
  loanId?: string;
  clubId?: string;
  principal: number; // capital original concedido en EUR (ej. 3000000)
  outstandingPrincipal: number; // capital pendiente de amortizar en EUR
  ratePerMatchday: number; // tipo de interés congelado por jornada (ej. 0.010 = 1,0%)
  firstMatchday: number; // primera jornada de cobro de intereses
  maturityMatchday: number; // jornada de vencimiento obligatorio
  termMatchdays?: number; // plazo en jornadas
  nextInterest: number; // interés estimado para el próximo cierre en EUR
  status: 'ACTIVE' | 'PAID_OFF' | string;
  createdAt?: string;
  settledAt?: string;
  movements?: LoanMovement[];
}

export interface MatchdayLoanOffer {
  maximumPrincipal: number; // capital máximo elegible en EUR
  remainingMatchdays: number; // jornadas restantes en la temporada
  maximumTermMatchdays: number; // jornadas de cobro hasta el vencimiento fijo
  maturityMatchday: number | null; // penúltima jornada del calendario oficial
  tier?: LoanTier; // 'TOP' | 'MIDDLE' | 'BOTTOM'
  ratePerMatchday: number; // tipo de interés por jornada (ej. 0.010 = 1,0%)
  ineligibilityReason?: LoanIneligibilityReason | null;
  activeLoan?: ActiveMatchdayLoan | null;
  calendar?: LoanOfferCalendar | null;
}

export interface MatchdayLoanItem {
  id: string;
  loanId?: string;
  clubId?: string;
  principal: number;
  outstandingPrincipal: number;
  ratePerMatchday: number;
  termMatchdays?: number;
  firstMatchday: number;
  maturityMatchday: number;
  nextInterest?: number;
  status: 'ACTIVE' | 'PAID_OFF' | string;
  createdAt?: string;
  settledAt?: string;
  movements?: LoanMovement[];
}

export interface AggregatedDebtResponse {
  totalDebt: number; // Suma capital de ambos modelos
  matchdayLoansDebt: number;
  legacyLoansDebt: number;
  nextInstallments?: Array<{
    installmentNumber?: number;
    amount: number;
    dueDate?: string;
    season?: number;
  }>;
}

export interface EconomicSummaryResponse {
  cash?: {
    totalBalance: number;
    reservedBalance: number;
    availableBalance: number;
    currency?: string;
  };
  totalBalance?: number;
  reservedBalance?: number;
  availableBalance?: number;
  currency?: string;
}

export interface LedgerEntry {
  id: string;
  timestamp: string;
  type: 'INGRESO' | 'GASTO' | 'RESERVA' | 'LIBERACION';
  amount: number; // in M€
  concept: string;
  referenceId?: string;
  runningBalance: number | null; // Backend balanceAfter converted to millions of EUR
  direction?: 'CREDIT' | 'DEBIT' | string;
  category?: string;
  sourceType?: string;
  sourceReference?: string;
  occurredAt?: string;
}

export interface ClubEconomy {
  availableBalance: number; // in M€ (initial: 100.0 M€)
  reservedBalance: number; // in M€
  totalBalance?: number; // in M€
  currency?: string;
  version?: number;
  ledger: LedgerEntry[];
  loans: BankLoan[];
  subscriptionCredits: number; // CR (initial: 1200 CR)
  monthlyFeeCr: number; // 100 CR/month
}

export interface BackendStadiumResponse {
  clubId: string;
  name?: string;
  stadiumName?: string;
  capacity: number;
  standsLevel: number;
  parkingLevel: number;
  cafeteriaLevel: number;
  merchandisingLevel: number;
  bathroomLevel?: number;
  condition?: number;
  maintenanceCost?: number;
  ticketPrice?: number;
  seasonTicketPrice?: number;
  ratingLevel?: number;
  facilitiesRatingLevel?: number;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
}

export interface BackendTicketPriceResponse {
  clubId: string;
  ticketPrice: number;
  version: number;
  effectiveAt?: string;
}

export type StadiumUpgradeModule = 'STANDS' | 'PARKING' | 'CAFETERIA' | 'MERCHANDISING' | 'BATHROOMS';

export type StadiumUpgradeOperationStatus =
  | 'REQUESTED'
  | 'FUNDS_CONFIRMED'
  | 'FUNDS_CAPTURED'
  | 'CAPTURE_PENDING'
  | 'CANCEL_REQUESTED'
  | 'REVERSAL_PENDING'
  | 'APPLIED'
  | 'FAILED'
  | 'CANCELLED';

export interface StadiumCatalogUpgradeOption {
  code: string;
  module: StadiumUpgradeModule;
  capacityIncrease?: number | null;
  targetLevel?: number | null;
  cost: number;
  durationMatchdays: number;
}

export interface StadiumCatalogResponse {
  clubId: string;
  initialCapacity: number;
  maximumCapacity: number;
  upgrades: StadiumCatalogUpgradeOption[];
}

export interface StadiumUpgradeQuoteResponse {
  module: StadiumUpgradeModule;
  upgradeCode: string;
  configurationToken: string;
  ruleSetVersion: number;
  quotedCost: number;
  stadiumVersion: number;
  durationMatchdays: number;
  fromLevel?: number | null;
  toLevel?: number | null;
  capacity?: number | null;
}

export interface StadiumUpgradeContractRequest {
  operationId: string;
  module: StadiumUpgradeModule;
  upgradeCode: string;
  configurationToken: string;
  ruleSetVersion: number;
  quotedCost: number;
  expectedStadiumVersion: number;
}

export interface UpgradeOperation {
  id: string;
  clubId: string;
  module: StadiumUpgradeModule;
  fromLevel?: number | null;
  toLevel?: number | null;
  ruleSetVersion: number;
  quotedCost: number;
  economyOperationId?: string | null;
  stadiumVersion: number;
  status: StadiumUpgradeOperationStatus;
  requestedAt?: string;
  appliedAt?: string | null;
  version: number;
}

export interface StadiumConstructionSchedule {
  seasonId: string;
  startMatchdayNumber: number;
  durationMatchdays: number;
  closedMatchdays: number;
  upgradeCode: string | null;
  targetCapacity: number | null;
}

export interface ConstructionStatus {
  clubId: string;
  operation: UpgradeOperation | null;
  schedule: StadiumConstructionSchedule | null;
  completionMatchdayNumber: number | null;
  remainingMatchdays: number | null;
}

// ==========================================
// SPONSORS (PATROCINIOS DEL CLUB)
// ==========================================

export type SponsorType = 'BILLBOARD' | 'SHIRT' | 'STADIUM';

export type SponsorContractStatus =
  | 'ACTIVE'
  | 'PENDING_ACTIVATION'
  | 'EXPIRED'
  | 'CANCELLED'
  | string;

export interface SponsorQuoteTerms {
  fixedAmount: number;
  winBonusAmount?: number | null;
  durationMatchdays?: number;
}

export interface SponsorMarketOffer {
  id: string;
  sponsorId?: string;
  sponsorName: string;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  type: SponsorType;
  durationMatchdays: number;
  quote?: SponsorQuoteTerms;
  fixedAmount?: number;
  winBonusAmount?: number | null;
  blockedReason?: string | null;
  status?: string;
  matchdayId?: string;
  rawOffer?: any;
  rawQuote?: any;
}

export interface SponsorContractTerms {
  fixedAmount: number;
  winBonusAmount?: number | null;
  durationMatchdays?: number;
}

export interface SponsorContract {
  id: string;
  clubId: string;
  offerId?: string;
  sponsorName: string;
  logoUrl?: string | null;
  type: SponsorType;
  status: SponsorContractStatus;
  economicModality?: string;
  terms?: SponsorContractTerms;
  fixedAmount?: number;
  winBonusAmount?: number | null;
  startMatchdayNumber: number;
  endMatchdayNumber: number;
  remainingMatchdays: number;
  accumulatedRevenue: number;
  signedAt?: string;
  effectiveAt?: string;
}

export interface SponsorContractsResponse {
  items?: SponsorContract[];
  content?: SponsorContract[];
  contracts?: SponsorContract[];
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
}

export interface ContractSponsorRequest {
  offerId: string;
}

export interface StadiumApiError {
  code?: string;
  message?: string;
  requestId?: string;
  detail?: string;
}

export interface BackendStaffMember {
  id: string;
  clubId: string;
  staffType: 'GOALKEEPER_COACH' | 'PHYSICAL_COACH' | 'SHOOTING_COACH' | 'TECHNICAL_COACH' | string;
  line: 'GOALKEEPER' | 'DEFENCE' | 'FORWARD' | 'MIDFIELD' | string;
  starRating: number;
  performanceModifierPct: number;
  annualCostReference: number;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
}

export interface BackendBalanceResponse {
  clubId: string;
  currency: string;
  totalBalance: number;
  reservedBalance: number;
  availableBalance: number;
  version?: number;
}

export interface BackendLedgerItem {
  id: string;
  direction: 'CREDIT' | 'DEBIT' | string;
  category: string;
  amount: number;
  balanceAfter?: number | null; // Posted balance in EUR
  concept: string;
  sourceType: string;
  sourceReference: string;
  occurredAt: string;
  recordedAt: string;
  reversalOfEntryId?: string | null;
}

export interface BackendLedgerResponse {
  content: BackendLedgerItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface ClubShieldConfig {
  templateId: string;
  primaryColor: string;
  secondaryColor: string;
  textColor?: string;
  initials: string;
}

export interface Club {
  id: string;
  name: string;
  shortName: string;
  code: string;
  primaryColor: string;
  secondaryColor: string;
  division: string;
  stadium: Stadium;
  staff: ClubStaff;
  economy: ClubEconomy;
  recentForm: ('W' | 'D' | 'L')[]; // Last 5 matches
  isUser: boolean;
  shield?: ClubShieldConfig | null;
}

export interface PlayerEditorialRating {
  playerId: string;
  playerName: string;
  position: Position;
  minutes: number;
  baseRating: number; // 1.0 to 5.0
  goals: number; // +3 pts each
  yellowCards: number; // -1 pt each
  redCards: number; // -3 pts each
  status: 'DRAFT' | 'PUBLISHED';
  revision: number;
  revisionReason?: string;
  calculatedPoints: number; // baseRating + (goals * 3) - (yellow * 1) - (red * 3)
}

export interface MatchEngineCalculation {
  homeTeamScore: number;
  awayTeamScore: number;
  homeRawPoints: number;
  awayRawPoints: number;
  modifiers: {
    homeStaffMod: number; // e.g. +3.5%
    awayStaffMod: number;
    attendanceMod: number; // e.g. +4.2%
    homeMomentumMod: number; // e.g. +3.0%
    awayMomentumMod: number; // e.g. +1.5%
  };
  homeEffectivePoints: number;
  awayEffectivePoints: number;
  attendanceCalculated: number;
  attendancePercentage: number;
  ticketRevenueM: number;
  commercialRevenueM: number;
  goalScorers: {
    minute: number;
    team: 'home' | 'away';
    playerName: string;
  }[];
}

export interface Fixture {
  id: string;
  matchdayNumber: number;
  homeClubId: string;
  awayClubId: string;
  homeScore?: number;
  awayScore?: number;
  played: boolean;
  engineCalculation?: MatchEngineCalculation;
}

export interface Matchday {
  number: number;
  season: number;
  status: MatchdayStatus;
  date: string;
  fixtures: Fixture[];
  id?: string;
  matchdayId?: string;
}

export interface StandingsRow {
  sportingMatchesPlayed?: number; administrativeLosses?: number; byes?: number;
  clubId: string;
  clubName: string;
  clubCode: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  recentForm: ('W' | 'D' | 'L')[];
  position?: number;
  shield?: ClubShieldConfig | null;
}

export interface GroupStandingItem {
  shield?: ClubShieldConfig | null;
  sportingMatchesPlayed?: number; administrativeLosses?: number; byes?: number;
  clubId: string;
  clubName: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  position: number;
  clubCode?: string;
  recentForm?: ('W' | 'D' | 'L')[];
}

export interface TransferOffer {
  id: string;
  playerId: string;
  fromClubId: string;
  toClubId: string | null; // null if free agent
  transferFeeM: number;
  offeredSalaryM: number;
  contractDurationYears: number;
  offeredReleaseClauseM: number;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'EFFECTIVE' | 'SUBMITTED' | string;
  effectiveMatchday?: number;
  type: 'FREE_AGENT' | 'CLUB_OFFER' | 'CLAUSE_BUYOUT' | string;
  createdOn: string;
  resolvedOn?: string;
  rejectionReason?: string;
  listingId?: string;
  playerInstanceId?: string;
  cutoffAt?: string;
  reservationId?: string;
  playerName?: string;
  position?: string;
  estimatedPlayerDecision?: 'LIKELY_REJECTED' | 'LIKELY_ACCEPTED' | string | null;
}

export type LeagueType = 'GLOBAL' | 'PRIVATE';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar?: string;
}

export interface LeagueSummary {
  id: string;
  name: string;
  description: string;
  type: LeagueType;
  code?: string;
  season: string;
  totalClubs: number;
  userClubId: string;
  userClubName: string;
  userClubCode: string;
  userPosition: number;
  userPoints: number;
  currentMatchdayNumber: number;
  totalMatchdays: number;
  isCreator?: boolean;
  activeUsersCount: number;
  badgeColor: string;
}

export interface LeagueContextData {
  league: LeagueSummary;
  clubs: Club[];
  userClub: Club;
  allPlayers: Player[];
  matchdays: Matchday[];
  currentMatchdayNumber: number;
  standings: StandingsRow[];
  lineupSlots: LineupSlot[];
  transferOffers: TransferOffer[];
}

export interface ApiCallLog {
  id: string;
  timestamp: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  statusCode: number;
  durationMs: number;
  requestBody?: any;
  responseBody?: any;
}

export interface UserParticipation {
  competitionId?: string;
  competitionName?: string;
  competitionUnitId?: string;
  unitId?: string;
  seasonId?: string;
  seasonName?: string;
  divisionId?: string;
  divisionName?: string;
  divisionLevel?: number;
  groupId?: string;
  groupName?: string;
  cycleNumber?: number;
  teamCount?: number | null;
  teamCapacity?: number | null;
  currentMatchday?: number | null;
  totalMatchdays?: number | null;
  club?: {
    id?: string;
    name?: string;
    code?: string;
  } | string | null;
  clubId?: string;
  clubName?: string;
  status?: string;
  active?: boolean;
}

export interface UserClubInfo {
  id?: string;
  name?: string;
  code?: string;
  primaryColor?: string;
}

export interface GlobalCompetitionInfo {
  competitionId: string;
  competitionUnitId?: string;
  unitId?: string;
  name?: string;
  status?: 'JOINED' | 'NOT_JOINED' | string;
  canJoin?: boolean;
  club?: UserClubInfo | string | null;
  clubId?: string;
  participations?: UserParticipation[];
  seasonId?: string;
  seasonName?: string;
  divisionId?: string;
  divisionName?: string;
  divisionLevel?: number;
  groupId?: string;
  groupName?: string;
  cycleNumber?: number;
  teamCount?: number | null;
  teamCapacity?: number | null;
  currentMatchday?: number | null;
  totalMatchdays?: number | null;
}

export interface PrivateLeagueBackendSummary {
  competitionId: string;
  privateLeagueId: string;
  type: 'PRIVATE';
  name: string;
  leagueState: 'DRAFT' | 'ACTIVE' | 'CANCELLING' | 'CANCELLED';
  membershipStatus: 'JOINED';
  club: { id: string; name: string };
  referenceSeasonId: string;
  privateSeasonId: string | null;
  effectiveJoiningRound: number | null;
  participations: { privateSeasonId: string; referenceSeasonId: string; currentReferenceRound: number | null }[];
  provisioning: { status: 'PENDING' | 'READY' | 'FAILED'; clubReady: boolean; squadReady: boolean; economyReady: boolean; errorCode: string | null };
  capabilities: { viewContext: boolean; play: boolean };
  unavailableReason: string | null;
  code?: string;
  description?: string;
  totalClubs?: number;
  currentMatchdayNumber?: number | null;
  totalMatchdays?: number | null;
  userClubName?: string;
}

export interface UserCompetitionsState {
  status?: 'JOINED' | 'NOT_JOINED' | string;
  state?: 'JOINED' | 'NOT_JOINED' | string;
  canJoin?: boolean;
  seasonId?: string;
  clubId?: string;
  competitionUnitId?: string;
  unitId?: string;
  global?: GlobalCompetitionInfo;
  club?: UserClubInfo | string | null;
  participations?: UserParticipation[];
  privateLeagues?: PrivateLeagueBackendSummary[];
  privateLeaguesEnabled?: boolean;
  privateLeaguesStatus?: 'AVAILABLE' | 'DISABLED';
}

export type CompetitionContextStatus = 'UNRESOLVED' | 'LOADING' | 'JOINED' | 'NOT_JOINED' | 'ERROR';

export interface ActiveCompetitionContext {
  mode?: 'GLOBAL' | 'PRIVATE';
  privateLeagueId?: string;
  status: CompetitionContextStatus;
  competitionId?: string;
  seasonId?: string;
  clubId?: string;
  competitionUnitId?: string;
  groupId?: string;
  divisionName?: string;
  divisionId?: string;
  divisionLevel?: number;
  clubName?: string;
  clubCode?: string;
  errorMessage?: string;
}

export interface DivisionStructure {
  id: string;
  level: number;
  name: string;
  format: string;
  clubsPerGroup: number;
  cyclesPerRealSeason: number;
  groups: any[];
}

export interface CompetitionStructure {
  seasonId: string;
  competitionId: string;
  seasonName: string;
  seasonStatus: string;
  startDate: string;
  endDate: string;
  divisions: DivisionStructure[];
}

export interface BackendSquadPlayer {
  id: string;
  playerDefinitionId: string;
  displayName: string;
  age?: number;
  realClub?: RealClubInfo;
  position: 'GOALKEEPER' | 'DEFENDER' | 'MIDFIELDER' | 'FORWARD' | Position | string;
  type: 'REAL' | 'AMATEUR' | 'PROFESSIONAL' | string;
  competitionUnitId?: string | null;
  ownerClubId?: string | null;
  status: string;
  currentMarketValue: number;
  version: number;
  secondaryPositions?: string[];
  ratingSeasonId?: string | null;
  averagePoints?: number | null;
  last3AveragePoints?: number | null;
  rating?: number | null;
  ratedAppearances?: number;
  unavailabilities?: PlayerUnavailability[];
  salary?: number;
  releaseClause?: number;
}

export interface ClubCoachRealClub {
  id: string;
  name: string;
  shortName: string;
}

export interface ClubCoach {
  id: string;
  name: string;
  imageUrl?: string | null;
  age?: number | null;
  realClub?: ClubCoachRealClub | null;
  attack?: number | null;
  midfield?: number | null;
  defense?: number | null;
  signed_at?: string | null;
  signedAt?: string | null;
}

export interface BackendSquadResponse {
  clubId: string;
  players: BackendSquadPlayer[];
  coach?: ClubCoach | null;
  _httpStatus?: number;
}

export interface BackendMarketPlayer {
  playerInstanceId: string;
  playerDefinitionId: string;
  displayName: string;
  age?: number;
  position: 'GOALKEEPER' | 'DEFENDER' | 'MIDFIELDER' | 'FORWARD' | Position | string;
  type: 'REAL' | 'AMATEUR' | string;
  marketValue: number; // En EUR (e.g. 5000000.00)
  expectedVersion?: number;
  realClub?: RealClubInfo;
  averagePoints?: number | null;
  last3AveragePoints?: number | null;
  unavailabilities?: PlayerUnavailability[];
}

export interface BackendClubListing {
  id: string;
  playerInstanceId: string;
  sellerClubId: string;
  competitionUnitId: string;
  askingPrice: number | null;
  status: string;
  createdAt: string;
  cancelledAt: string | null;
  player: BackendMarketPlayer;
}

export interface BackendMarketListingResponse {
  id: string;
  competitionUnitId: string;
  marketDate: string;
  generatedAt: string;
  closesAt?: string;
  closes_at?: string;
  players: BackendMarketPlayer[];
  _httpStatus?: number;
}

export interface MarketOfferTerms {
  transferAmount: number;
  annualSalary: number;
  releaseClause: number;
  termSeasons: number;
}

export interface MarketOfferFailure {
  code: string; // e.g. 'INSUFFICIENT_FUNDS'
  message?: string;
  requestedAmount?: number;
  availableAmount?: number;
  [key: string]: any;
}

export interface MarketOfferResponse {
  id?: string;
  listingId?: string;
  playerInstanceId?: string;
  buyerClubId?: string;
  terms?: MarketOfferTerms;
  submittedAt?: string;
  cutoffAt?: string;
  reservationExpiresAt?: string;
  status: string; // e.g. 'SUBMITTED', 'FAILED'
  failure?: MarketOfferFailure;
  reservationId?: string;
  version?: number;
  estimatedPlayerDecision?: 'LIKELY_REJECTED' | 'LIKELY_ACCEPTED' | string | null;
  _httpStatus?: number;
  _isMock?: boolean;
}

export interface BackendActiveOffer {
  id: string;
  listingId: string;
  playerInstanceId: string;
  buyerClubId: string;
  terms: MarketOfferTerms;
  submittedAt: string;
  cutoffAt?: string;
  reservationExpiresAt?: string;
  status: string; // e.g. 'SUBMITTED'
  reservationId?: string;
  version: number;
  player: BackendMarketPlayer;
  estimatedPlayerDecision?: 'LIKELY_REJECTED' | 'LIKELY_ACCEPTED' | string | null;
  _httpStatus?: number;
  _isMock?: boolean;
}

export type BackendActiveOffersResponse =
  | BackendActiveOffer[]
  | {
      id?: string;
      competitionUnitId?: string;
      marketDate?: string;
      generatedAt?: string;
      players?: BackendMarketPlayer[];
      offers?: BackendActiveOffer[];
      _httpStatus?: number;
      _isMock?: boolean;
    };

export interface ActiveOfferItem {
  playerDefinitionId?: string;
  isAmateur?: boolean;
  source?: 'FREE' | 'CLUB';
  operationType?: string;
  clubDecision?: string;
  playerDecision?: string;
  marketValueKnown?: boolean;
  id: string;
  listingId: string;
  playerInstanceId: string;
  displayName: string;
  position: string;
  marketValue: number;
  transferAmount: number;
  annualSalary: number;
  releaseClause: number;
  termSeasons: number;
  status: string; // 'SUBMITTED' | 'ACTIVE' | 'PENDING' | 'ACCEPTED' | 'REJECTED'
  submittedAt?: string;
  cutoffAt?: string;
  reservationId?: string;
  reservationExpiresAt?: string;
  version?: number;
  hasUserOffer: boolean;
  estimatedPlayerDecision?: 'LIKELY_REJECTED' | 'LIKELY_ACCEPTED' | string | null;
}

export interface BackendClubTransfer {
  id: string;
  updateId?: string;
  updateAccepted?: boolean | null;
  listingId: string | null;
  playerInstanceId: string;
  buyerClubId: string | null;
  sellerClubId: string;
  type: string;
  status: string;
  releaseOutcome?: string | null;
  estimatedPlayerDecision?: 'LIKELY_REJECTED' | 'LIKELY_ACCEPTED' | string | null;
  clubDecision: 'PENDING' | 'ACCEPTED' | 'REJECTED' | string;
  playerDecision?: string;
  version: number;
  terms: {
    transferAmount: number;
    annualSalary?: number;
    releaseClause?: number;
    termSeasons?: number;
  };
  player?: {
    playerDefinitionId?: string;
    competitionUnitId?: string;
    displayName?: string;
    name?: string;
    position?: string;
    age?: number;
    currentMarketValue?: number;
    [key: string]: any;
  } | null;
  roundId?: string | number | null;
  presentedAt?: string;
  submittedAt?: string;
  decisionDeadline?: string;
  executionCutoff?: string;
  reservationId?: string;
  buyerClubName?: string;
  buyerClub?: { id?: string; name?: string; code?: string; shortName?: string } | null;
}

export type OfferRejectionReason =
  | 'ACCEPTED_OTHER_CLUB'
  | 'RELEASE_CLAUSE_TOO_HIGH'
  | 'ECONOMIC_OFFER_TOO_LOW'
  | string;

export interface BackendHistoricalOfferItem {
  id: string;
  listingId: string | null;
  offerType?: 'FREE_AGENT' | 'NEGOTIATED' | 'RELEASE_CLAUSE' | string;
  transferStatus?: string | null;
  transferReason?: string | null;
  boardLimit?: number | null;
  playerInstanceId: string;
  buyerClubId: string;
  terms: {
    transferAmount: number;
    annualSalary: number;
    releaseClause: number;
    termSeasons: number;
  };
  submittedAt: string;
  cutoffAt?: string | null;
  reservationExpiresAt?: string | null;
  status: 'WON' | 'LOST' | 'CANCELLED' | 'SUBMITTED' | string;
  rejectionReason?: OfferRejectionReason | null;
  reservationId?: string;
  version?: number;
  failure?: any;
  playerDefinitionId?: string;
  playerName?: string;
  playerDataAvailable?: boolean;
  position?: string;
}

export interface BackendHistoricalOffersResponse {
  content: BackendHistoricalOfferItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  _httpStatus?: number;
  _isMock?: boolean;
}

export interface BackendClubReference {
  id: string;
  code: string;
  name: string;
}

export interface BackendFixture {
  id: string;
  homeClub: BackendClubReference;
  awayClub: BackendClubReference;
  status: string;
  homeGoals: number | null;
  awayGoals: number | null;
  phase?: string;
  playoffType?: string;
}

export interface CompetitionMatchdayResponse {
  seasonId: string;
  club: BackendClubReference;
  matchdayNumber: number;
  totalMatchdays: number;
  currentMatchdayNumber: number;
  previousMatchdayNumber: number | null;
  nextMatchdayNumber: number | null;
  startsAt: string;
  endsAt: string;
  windowStatus: string;
  competitionUnitId: string;
  groupName: string;
  cycleNumber: number;
  matchdayId: string;
  localMatchdayNumber: number;
  status: string;
  scheduleStatus: string;
  clubResting: boolean;
  restingClubs?: BackendClubReference[];
  fixtures: BackendFixture[];
  phase?: string;
  playoffType?: string;
  _httpStatus?: number;
}

export interface NextMatchSponsor {
  name: string;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  category: 'STADIUM' | 'SHIRT' | 'BILLBOARD' | string;
}

export interface NextMatchClub {
  id: string;
  code: string;
  name: string;
  sponsors: NextMatchSponsor[];
}

export interface NextMatchStadium {
  ratingLevel: number;
  facilitiesRatingLevel: number;
  baseName?: string;
  name: string;
  capacity: number;
}

export interface NextMatchResponse {
  fixtureId: string;
  seasonId: string;
  competitionUnitId: string;
  groupName: string;
  matchdayId: string;
  matchdayNumber: number;
  localMatchdayNumber: number;
  startsAt: string;
  endsAt: string;
  status: string;
  matchdayStatus: MatchdayStatus;
  venue: 'HOME' | 'AWAY' | string;
  homeClub: NextMatchClub;
  awayClub: NextMatchClub;
  stadium?: NextMatchStadium;
  phase?: 'REGULAR' | 'PLAYOFF' | string;
  cycleNumber?: number;
  playoffType?: 'MAIN' | string;
  _httpStatus?: number;
  _isMock?: boolean;
}

export interface NewsFactTransfer {
  demo?: boolean;
  amount?: number;
  currency?: string;
  playerName?: string;
  transferId?: string;
  buyerClubId?: string | null;
  sellerClubId?: string | null;
  transferType?: string;
  buyerClubName?: string | null;
  sellerClubName?: string | null;
  playerInstanceId?: string | null;
  playerDefinitionId?: string | null;
  effectiveMatchdayId?: string | null;
  [key: string]: any;
}

export interface NewsItem {
  id: string;
  type: string;
  templateVersion: number;
  competitionUnitId: string;
  seasonId?: string;
  occurredAt: string;
  publishedAt: string;
  title: string;
  titleHtml?: string;
  facts: NewsFactTransfer;
}

export interface NewsResponse {
  items: NewsItem[];
  nextCursor?: string | null;
}

export interface MatchLivePlayer {
  playerInstanceId: string;
  playerDefinitionId?: string;
  displayName?: string;
  line: 'GOALKEEPER' | 'DEFENCE' | 'MIDFIELD' | 'FORWARD' | string;
  type: 'REAL' | 'AMATEUR' | string;
  status: 'PENDING' | 'AVAILABLE' | string;
  points: number | null;
  performanceId?: string | null;
  performanceRevision?: number | null;
  realClub?: {
    id?: string;
    name?: string;
    shortName?: string;
  } | null;
}

export interface MatchLiveStaffRatings {
  DEFENCE?: number;
  GOALKEEPER?: number;
  MIDFIELD?: number;
  FORWARD?: number;
  [key: string]: number | undefined;
}

export interface MatchLiveScore {
  clubId: string;
  attendanceModifierPercent?: number;
  formModifierPercent?: number;
  goals?: number;
  optionB?: {
    staffModifierPercents?: MatchLiveStaffRatings;
    staffAdjustedPower?: {
      attack?: number;
      midfield?: number;
      defense?: number;
    };
    offensiveStrength?: number;
    afterAttendance?: number;
    goalInput?: number;
  };
}

export interface LiveBenchPlayer {
  playerInstanceId: string;
  playerDefinitionId?: string;
  position?: string;
  type?: string;
  benchPlace?: number;
  retired?: boolean;
  displayName?: string;
  realClub?: {
    id?: string;
    name?: string;
    shortName?: string;
  } | null;
}

export interface MatchLiveTeam {
  clubId: string;
  staffRatings?: MatchLiveStaffRatings;
  recentOutcomes?: any[];
  players: MatchLivePlayer[];
  bench?: LiveBenchPlayer[];
  substitutionWindows?: SubstitutionWindow[];
  windowsUsed?: number;
  substitutionsUsed?: number;
  lineupRevision?: number;
  calculatedLineupRevision?: number | null;
  score?: MatchLiveScore;
}

export interface MatchLiveResponse {
  fixtureId: string;
  matchdayId?: string;
  status: 'WAITING_FOR_POINTS' | 'IN_PROGRESS' | 'FINISHED' | 'PLANNED' | string;
  modifiersFrozenAt?: string;
  ruleSetVersion?: number;
  attendancePercent?: number;
  home: MatchLiveTeam;
  away: MatchLiveTeam;
  homeGoals?: number | null;
  awayGoals?: number | null;
  updatedAt?: string;
}

export type {
  PortfolioDiscoveryStatus,
  PortfolioItem,
  PortfolioResponse,
  ReleaseClauseAttemptPayload,
  ReleaseClauseAttemptResponse,
  ContractRenewalPayload,
  ContractRenewalResponse,
} from '../simulation/portfolio-simulation';

